import { Container, Sprite, type Texture } from 'pixi.js';
import { VIRTUAL_HEIGHT, VIRTUAL_WIDTH } from '@/core/viewport';
import { TILE_SIZE, type TileSpriteKey } from '@/sprites/tiles';
import { TILE_KINDS, type Tilemap } from '@/world/tilemap';

/**
 * Culled tilemap renderer.
 *
 * Only tiles intersecting the view are given sprites, and the sprite pool is
 * reused across frames. Drawing a whole 40x30 map every frame would work at
 * this size but stops working the moment maps get bigger — and the pool is
 * cheap enough that there is no reason to write the naive version first.
 */
export class TilemapRenderer {
  readonly container = new Container();

  private readonly pool: Sprite[] = [];
  private readonly textures: Readonly<Record<TileSpriteKey, Texture>>;

  constructor(
    private readonly map: Tilemap,
    textures: Readonly<Record<TileSpriteKey, Texture>>,
  ) {
    this.textures = textures;
  }

  /** Redraw for a camera at (cameraX, cameraY) in world pixels. */
  render(cameraX: number, cameraY: number): void {
    // One extra tile each side so partially-visible edges are not popped in.
    const minX = Math.max(0, Math.floor(cameraX / TILE_SIZE) - 1);
    const minY = Math.max(0, Math.floor(cameraY / TILE_SIZE) - 1);
    const maxX = Math.min(this.map.width - 1, Math.ceil((cameraX + VIRTUAL_WIDTH) / TILE_SIZE));
    const maxY = Math.min(this.map.height - 1, Math.ceil((cameraY + VIRTUAL_HEIGHT) / TILE_SIZE));

    let used = 0;

    for (let ty = minY; ty <= maxY; ty++) {
      for (let tx = minX; tx <= maxX; tx++) {
        const kind = TILE_KINDS[this.map.at(tx, ty)];

        // Ground first, then the prop over it. Sprites within one tile keep
        // their relative order because the pool is drawn in index order.
        if (kind.base) {
          const ground = this.spriteAt(used++);
          ground.texture = this.textures[kind.base];
          ground.position.set(tx * TILE_SIZE, ty * TILE_SIZE);
          ground.visible = true;
        }

        const sprite = this.spriteAt(used++);
        sprite.texture = this.textures[kind.sprite];
        sprite.position.set(tx * TILE_SIZE, ty * TILE_SIZE);
        sprite.visible = true;
      }
    }

    // Park the surplus rather than destroying it; the count is stable frame to
    // frame, so this costs nothing after the first frame.
    for (let i = used; i < this.pool.length; i++) {
      this.pool[i]!.visible = false;
    }
  }

  /** Release the sprite pool. Called when the player leaves the area. */
  destroy(): void {
    this.container.destroy({ children: true });
    this.pool.length = 0;
  }

  private spriteAt(index: number): Sprite {
    let sprite = this.pool[index];
    if (!sprite) {
      sprite = new Sprite();
      this.pool.push(sprite);
      this.container.addChild(sprite);
    }
    return sprite;
  }
}
